/**
 * Cash drawer page — ``/finance/cash``.
 *
 * Three primary affordances:
 *
 * 1. Stats row at the top showing the expected drawer balance, the last
 *    recorded count + variance, and the "Of which Pfand" derived figure
 *    (computed server-side as Σ pfand_value_cents across drawer-affecting
 *    expense rows — never an operator input).
 * 2. Quick-action CTAs to (a) record a physical cash count, (b) settle a
 *    guest in cash, (c) record a one-off cash expense, (d) pay a
 *    procurement out of the drawer.
 * 3. A recent-activity feed (last 50 cash movements: cash settlements +
 *    drawer-affecting expenses unioned + sorted desc).
 *
 * KEY DESIGN POINT: the count modal takes a SINGLE counted total. The
 * operator can't separate Pfand cash from regular cash in a physical
 * drawer; the Pfand allocation is derived from the bookkeeping. This is
 * deliberate per the 2026-06-04 spec note.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Form,
  Grid,
  Input,
  Modal,
  Radio,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import type { Dayjs } from "dayjs";
import { NumInput as InputNumber } from "../../components/NumInput";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { Link } from "react-router-dom";

import {
  ApiError,
  type CashActivityRow,
  type CashDrawerMonthEndRow,
  type CashDrawerSnapshot,
  type CashDrawerState,
  type ExpenseCategoryRow,
  type ProcurementRow,
  api,
} from "../../api/client";
import { OPERATOR_HIDDEN_FOR_CASH_DRAWER } from "../../utils/expenseCategoryCodes";
// TEMPORARY: settle a partial amount computed from a user's recent article
// purchases (stopgap until the structured article-reference standard lands).
import { PurchaseSettleWidget } from "./PurchaseSettleWidget";

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const formatCents = (c: number | null | undefined): string => {
  if (c === null || c === undefined) return "—";
  const euros = c / 100;
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
  }).format(euros);
};

const formatSignedCents = (c: number | null | undefined): string => {
  if (c === null || c === undefined) return "—";
  const sign = c > 0 ? "+" : c < 0 ? "−" : "";
  return `${sign}${formatCents(Math.abs(c))}`;
};

const formatDateTime = (iso: string): string =>
  dayjs(iso).format("DD.MM.YYYY HH:mm");

const ACTIVITY_KIND_COLORS: Record<CashActivityRow["kind"], string> = {
  settlement: "blue",
  expense: "default",
  variance: "orange",
  opening: "purple",
  procurement_payment: "geekblue",
};

interface CashExpenseFormValues {
  direction: "in" | "out";
  value_magnitude: number | null;
  pfand_magnitude: number | null;
  category_id: number;
  comment?: string | null;
}

/**
 * Full-screen "Set up the cash drawer" experience. Renders instead of the
 * regular dashboard until the operator records the initial cash balance
 * via the ``/cash-drawer/open`` endpoint (server resolves the category).
 *
 * Detection lives in the parent (FinanceCashPage); this component is purely
 * presentational + handles its own submit.
 *
 * Conflict handling: the server returns 409 when an active opening already
 * exists. The form surfaces the conflict message + a "Record anyway"
 * affordance that re-submits with ``force=true`` (which intentionally
 * creates a duplicate row — the parent renders a multi-opening warning
 * banner so the operator can hunt the extras down).
 */
interface CashDrawerSetupProps {
  onOpened: () => Promise<void> | void;
}

const CashDrawerSetup = ({ onOpened }: CashDrawerSetupProps) => {
  const [initialEuros, setInitialEuros] = useState<number | null>(null);
  const [comment, setComment] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Set when the server 409s an opening attempt — switches the CTA from
  // "Open cash drawer" to the duplicate-creation confirmation.
  const [conflict, setConflict] = useState<string | null>(null);

  const canSubmit =
    initialEuros !== null && initialEuros > 0 && !submitting;

  const doOpen = async (force: boolean) => {
    if (initialEuros === null || initialEuros <= 0) {
      setError("Initial cash must be greater than 0.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await api.openCashDrawer({
        value_cents: Math.round(initialEuros * 100),
        comment: comment.trim() || null,
        ...(force ? { force: true } : {}),
      });
      setConflict(null);
      await onOpened();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setConflict(err.message);
      } else {
        setError(err instanceof ApiError ? err.message : String(err));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = () => {
    // Clear any prior conflict state on a fresh submit attempt; the user
    // may have edited the inputs since the 409.
    setConflict(null);
    void doOpen(false);
  };

  const handleForceSubmit = () => {
    void doOpen(true);
  };

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "60vh",
        padding: 16,
      }}
    >
      <Card style={{ maxWidth: 480, width: "100%" }}>
        <Title level={3} style={{ marginTop: 0 }}>
          Set up the cash drawer
        </Title>
        <Paragraph type="secondary">
          Your cash drawer hasn&apos;t been opened yet. Enter the cash
          you&apos;re starting with to begin tracking inflows and outflows.
        </Paragraph>
        {error && (
          <Alert
            type="error"
            message={error}
            style={{ marginBottom: 16 }}
            showIcon
            closable
            onClose={() => setError(null)}
          />
        )}
        {conflict && (
          <Alert
            type="warning"
            showIcon
            message="Cash drawer already open"
            description={conflict}
            style={{ marginBottom: 16 }}
          />
        )}
        <Form layout="vertical" onFinish={handleSubmit}>
          <Form.Item label="Initial cash on hand (€)" required>
            <InputNumber<number>
              min={0}
              step={0.01}
              precision={2}
              placeholder="e.g. 100.00"
              style={{ width: "100%" }}
              value={initialEuros ?? undefined}
              onChange={(v) =>
                setInitialEuros(v === null || v === undefined ? null : v)
              }
              disabled={submitting}
              autoFocus
            />
          </Form.Item>
          <Form.Item label="Comment (optional)">
            <TextArea
              rows={2}
              maxLength={500}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              disabled={submitting}
            />
          </Form.Item>
          {conflict ? (
            <Space direction="vertical" style={{ width: "100%" }}>
              <Button
                danger
                block
                loading={submitting}
                disabled={!canSubmit}
                onClick={handleForceSubmit}
              >
                Record anyway, this will create a duplicate opening
              </Button>
              <Button
                block
                disabled={submitting}
                onClick={() => setConflict(null)}
              >
                Cancel
              </Button>
            </Space>
          ) : (
            <Button
              type="primary"
              htmlType="submit"
              block
              loading={submitting}
              disabled={!canSubmit}
            >
              Open cash drawer
            </Button>
          )}
        </Form>
      </Card>
    </div>
  );
};

export const FinanceCashPage = () => {
  const [state, setState] = useState<CashDrawerState | null>(null);
  const [categories, setCategories] = useState<ExpenseCategoryRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Modal open state
  const [countModalOpen, setCountModalOpen] = useState(false);
  const [expenseModalOpen, setExpenseModalOpen] = useState(false);
  const [procurementModalOpen, setProcurementModalOpen] = useState(false);

  // Bulk buy: quick-charge N × article to a strichliste user directly
  // from the drawer surface (kiosk-parity with the admin-extras overlay's
  // `+` affordance, so the operator doesn't have to walk to the kiosk to
  // ring up "5 Sternburg for Alice"). Reuses the same POST /users/{sid}/buy
  // path as ArticleBuyPane, now taking an optional quantity.
  const [bulkBuyOpen, setBulkBuyOpen] = useState(false);
  const [bulkBuyUserId, setBulkBuyUserId] = useState<number | null>(null);
  const [bulkBuyChainId, setBulkBuyChainId] = useState<number | null>(null);
  const [bulkBuyQty, setBulkBuyQty] = useState<number>(1);
  const [bulkBuyChains, setBulkBuyChains] = useState<
    { chain_root_id: number; current_leaf_id: number; name: string; sell_price_cents: number }[]
  >([]);
  const [bulkBuyUserOptions, setBulkBuyUserOptions] = useState<
    { value: number; label: string; balance_cents: number | null }[]
  >([]);
  const [bulkBuyUserSearch, setBulkBuyUserSearch] = useState<string>("");
  const [bulkBuySubmitting, setBulkBuySubmitting] = useState(false);
  const bulkBuyDebounce = useRef<number | null>(null);

  // Count modal — operator types ONE number.
  const [countedCents, setCountedCents] = useState<number | null>(null);
  const [countComment, setCountComment] = useState<string>("");

  // Cash expense modal
  const [expenseForm] = Form.useForm<CashExpenseFormValues>();
  const watchedDirection = Form.useWatch("direction", expenseForm) as
    | "in"
    | "out"
    | undefined;

  // Procurement modal
  const [procurements, setProcurements] = useState<ProcurementRow[]>([]);
  const [unpaidProcurementIds, setUnpaidProcurementIds] = useState<Set<number>>(
    new Set(),
  );
  const [selectedProcurementId, setSelectedProcurementId] = useState<
    number | null
  >(null);
  const [procurementComment, setProcurementComment] = useState<string>("");

  // Historical snapshot picker + month-end overview
  const [snapshotAt, setSnapshotAt] = useState<Dayjs | null>(null);
  const [snapshot, setSnapshot] = useState<CashDrawerSnapshot | null>(null);
  const [snapshotLoading, setSnapshotLoading] = useState(false);
  const [snapshotError, setSnapshotError] = useState<string | null>(null);
  const [monthEndYear, setMonthEndYear] = useState<number>(
    () => new Date().getUTCFullYear(),
  );
  const [monthEnd, setMonthEnd] = useState<CashDrawerMonthEndRow[]>([]);
  const [monthEndLoading, setMonthEndLoading] = useState(false);
  const [monthEndError, setMonthEndError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, cats] = await Promise.all([
        api.getCashDrawerState(),
        api.listExpenseCategories(),
      ]);
      setState(s);
      setCategories(cats);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Bulk-buy: load the article catalog once when the modal first opens.
  // Reuses `listArticleChains` (same source as ArticleBuyPane) so a
  // separate proxy isn't needed.
  useEffect(() => {
    if (!bulkBuyOpen || bulkBuyChains.length > 0) return;
    let cancelled = false;
    api.listArticleChains().then((rows) => {
      if (cancelled) return;
      setBulkBuyChains(
        rows
          .filter((r) => r.active)
          .map((r) => ({
            chain_root_id: r.chain_root_id,
            current_leaf_id: r.current_leaf_id,
            name: r.name,
            sell_price_cents: r.sell_price_cents,
          })),
      );
    });
    return () => {
      cancelled = true;
    };
  }, [bulkBuyOpen, bulkBuyChains.length]);

  // Bulk-buy: debounced strichliste-user search feeding the picker.
  useEffect(() => {
    if (!bulkBuyOpen) return;
    if (bulkBuyDebounce.current)
      window.clearTimeout(bulkBuyDebounce.current);
    bulkBuyDebounce.current = window.setTimeout(() => {
      void api
        .listUsers({
          q: bulkBuyUserSearch.trim() || undefined,
          status: "active",
          page_size: 25,
        })
        .then((page) => {
          setBulkBuyUserOptions(
            page.items
              .filter((u) => u.user_type !== "bank" && !u.disabled)
              .map((u) => ({
                value: u.strichliste_user_id,
                label: u.real_name
                  ? `${u.real_name} (${u.name})`
                  : u.name,
                balance_cents: u.balance_cents,
              })),
          );
        })
        .catch(() => setBulkBuyUserOptions([]));
    }, 200);
    return () => {
      if (bulkBuyDebounce.current)
        window.clearTimeout(bulkBuyDebounce.current);
    };
  }, [bulkBuyOpen, bulkBuyUserSearch]);

  const openBulkBuyModal = () => {
    setBulkBuyUserId(null);
    setBulkBuyChainId(null);
    setBulkBuyQty(1);
    setBulkBuyUserSearch("");
    setBulkBuyOpen(true);
  };

  const bulkBuySelectedChain = useMemo(
    () => bulkBuyChains.find((c) => c.chain_root_id === bulkBuyChainId) ?? null,
    [bulkBuyChains, bulkBuyChainId],
  );
  const bulkBuySelectedUser = useMemo(
    () => bulkBuyUserOptions.find((u) => u.value === bulkBuyUserId) ?? null,
    [bulkBuyUserOptions, bulkBuyUserId],
  );
  const bulkBuyTotalCents = useMemo(
    () =>
      bulkBuySelectedChain
        ? bulkBuySelectedChain.sell_price_cents * bulkBuyQty
        : 0,
    [bulkBuySelectedChain, bulkBuyQty],
  );

  const submitBulkBuy = async () => {
    if (bulkBuyUserId === null || !bulkBuySelectedChain) return;
    setBulkBuySubmitting(true);
    try {
      const res = await api.buyArticleForUser(
        bulkBuyUserId,
        bulkBuySelectedChain.current_leaf_id,
        bulkBuyQty,
      );
      message.success(
        `Bought ${bulkBuyQty} × ${bulkBuySelectedChain.name} for ${bulkBuySelectedUser?.label ?? "user"} (${formatCents(-res.amount_cents)})`,
      );
      setBulkBuyOpen(false);
      // No drawer flow to refresh — the buy hits the user's balance,
      // not the drawer ledger. Recent activity is unchanged.
    } catch (err) {
      message.error(
        err instanceof ApiError ? err.message : String(err),
      );
    } finally {
      setBulkBuySubmitting(false);
    }
  };

  // Historical snapshot: fetch on every picker change (debounced by the
  // picker itself — Ok click, not per-keystroke).
  useEffect(() => {
    if (!snapshotAt) {
      setSnapshot(null);
      setSnapshotError(null);
      // Reset the loading flag too — a mid-fetch clear leaves an in-flight
      // request whose finally() is guarded by `cancelled`; without this
      // reset the spinner would stick until the next successful fetch.
      setSnapshotLoading(false);
      return;
    }
    let cancelled = false;
    setSnapshotLoading(true);
    setSnapshotError(null);
    api
      .getCashDrawerSnapshot(snapshotAt.toISOString())
      .then((s) => {
        if (!cancelled) setSnapshot(s);
      })
      .catch((err) => {
        if (!cancelled)
          setSnapshotError(
            err instanceof ApiError ? err.message : String(err),
          );
      })
      .finally(() => {
        if (!cancelled) setSnapshotLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [snapshotAt]);

  // Month-end: refetch on year change (initial mount + user picks a
  // different year).
  useEffect(() => {
    let cancelled = false;
    setMonthEndLoading(true);
    setMonthEndError(null);
    api
      .getCashDrawerMonthEnd(monthEndYear)
      .then((rows) => {
        if (!cancelled) {
          setMonthEnd(rows);
          setMonthEndError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setMonthEnd([]);
          // Surface the error so the operator distinguishes 'server said no'
          // from 'genuinely no rows'. Otherwise a 400 (year < 2000) or 500
          // renders as the misleading 'all in the future?' empty state.
          setMonthEndError(
            err instanceof ApiError ? err.message : String(err),
          );
        }
      })
      .finally(() => {
        if (!cancelled) setMonthEndLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [monthEndYear]);

  // -------------------------------------------------------------------------
  // Record-count modal
  // -------------------------------------------------------------------------

  const expectedCents = state?.expected_balance_cents ?? 0;
  const liveVariance =
    countedCents !== null ? countedCents - expectedCents : null;

  const submitCount = async () => {
    if (countedCents === null || countedCents < 0) {
      setError("Counted amount must be ≥ 0");
      return;
    }
    setSaving(true);
    try {
      const next = await api.recordCashDrawerCount({
        counted_cents: countedCents,
        comment: countComment.trim() || undefined,
      });
      setState(next);
      setCountModalOpen(false);
      setCountedCents(null);
      setCountComment("");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  // -------------------------------------------------------------------------
  // Cash expense modal
  // -------------------------------------------------------------------------

  // Looked up by immutable ``code`` (post-migration-0024) — survives any
  // operator-driven rename of the display name.
  const cashDrawerCategoryId = useMemo(
    () => categories.find((c) => c.code === "cash_drawer")?.id,
    [categories],
  );

  const openExpenseModal = () => {
    expenseForm.resetFields();
    if (cashDrawerCategoryId !== undefined) {
      expenseForm.setFieldsValue({
        direction: "out",
        category_id: cashDrawerCategoryId,
        value_magnitude: null,
        pfand_magnitude: null,
      });
    } else {
      expenseForm.setFieldsValue({ direction: "out" });
    }
    setExpenseModalOpen(true);
  };

  const submitExpense = async () => {
    try {
      const values = await expenseForm.validateFields();
      const sign = values.direction === "in" ? 1 : -1;
      const valueCents =
        values.value_magnitude !== null && values.value_magnitude !== undefined
          ? sign * Math.round(values.value_magnitude)
          : 0;
      const pfandCents =
        values.pfand_magnitude !== null && values.pfand_magnitude !== undefined
          ? sign * Math.round(values.pfand_magnitude)
          : 0;
      if (valueCents === 0 && pfandCents === 0) {
        setError("Enter a non-zero value or Pfand magnitude.");
        return;
      }
      setSaving(true);
      await api.createExpense({
        category_id: values.category_id,
        value_cents: valueCents,
        pfand_value_cents: pfandCents !== 0 ? pfandCents : null,
        comment: values.comment?.trim() || null,
      });
      setExpenseModalOpen(false);
      await refresh();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      }
      // form validation errors are surfaced by AntD inline
    } finally {
      setSaving(false);
    }
  };

  // -------------------------------------------------------------------------
  // Pay-procurement modal
  // -------------------------------------------------------------------------

  const openProcurementModal = async () => {
    setProcurementModalOpen(true);
    setSelectedProcurementId(null);
    setProcurementComment("");
    setSaving(true);
    try {
      // Load received procurements; we'll filter client-side to those
      // without an existing Cash-drawer expense.
      const list = await api.listProcurements({
        state: "received",
        limit: 100,
      });
      setProcurements(list);
      // Probe each for an existing cash-drawer expense via the existing
      // listProcurementExpenses endpoint. This is N+1 but bounded by the
      // 100-row cap and only fires when the modal opens.
      const unpaid = new Set<number>();
      await Promise.all(
        list.map(async (p) => {
          const expenses = await api.listProcurementExpenses(p.id);
          const cashId = categories.find((c) => c.code === "cash_drawer")?.id;
          const alreadyPaid = expenses.some(
            (e) => e.category_id === cashId,
          );
          if (!alreadyPaid) unpaid.add(p.id);
        }),
      );
      setUnpaidProcurementIds(unpaid);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const selectedProcurement = useMemo(
    () =>
      selectedProcurementId !== null
        ? procurements.find((p) => p.id === selectedProcurementId) ?? null
        : null,
    [procurements, selectedProcurementId],
  );

  const submitProcurementPayment = async () => {
    if (selectedProcurementId === null) {
      setError("Select a procurement to pay.");
      return;
    }
    setSaving(true);
    try {
      await api.payProcurementFromDrawer(
        selectedProcurementId,
        procurementComment.trim() || undefined,
      );
      setProcurementModalOpen(false);
      setSelectedProcurementId(null);
      setProcurementComment("");
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  // -------------------------------------------------------------------------
  // Activity table columns
  // -------------------------------------------------------------------------

  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  // On mobile we strip the table down to Date / Flow / What — drop the Pfand +
  // Link columns, compact the date, and ellipsis the description so it fits the
  // screen without horizontal scrolling. Desktop keeps the full set.
  const activityColumns: ColumnsType<CashActivityRow> = isMobile
    ? [
        {
          title: "Date",
          dataIndex: "when",
          width: 96,
          render: (v: string) => (
            <Text style={{ fontSize: 12, whiteSpace: "nowrap" }}>
              {dayjs(v).format("DD.MM. HH:mm")}
            </Text>
          ),
        },
        {
          title: "Flow",
          dataIndex: "flow_cents",
          align: "right" as const,
          width: 78,
          render: (v: number) => (
            <Text
              style={{
                fontSize: 12,
                whiteSpace: "nowrap",
                color: v > 0 ? "#52c41a" : v < 0 ? "#cf1322" : undefined,
              }}
            >
              {formatSignedCents(v)}
            </Text>
          ),
        },
        {
          title: "What",
          key: "kind_and_description",
          ellipsis: true,
          render: (_: unknown, row) => (
            <span style={{ fontSize: 12 }}>
              <Tag
                color={ACTIVITY_KIND_COLORS[row.kind]}
                style={{ marginInlineEnd: 4 }}
              >
                {row.kind}
              </Tag>
              {row.description}
            </span>
          ),
        },
      ]
    : [
        {
          title: "Date",
          dataIndex: "when",
          render: (v: string) => formatDateTime(v),
          width: 160,
        },
        {
          title: "Flow",
          dataIndex: "flow_cents",
          align: "right" as const,
          render: (v: number) => (
            <Text
              style={{ color: v > 0 ? "#52c41a" : v < 0 ? "#cf1322" : undefined }}
            >
              {formatSignedCents(v)}
            </Text>
          ),
          width: 120,
        },
        {
          title: "Of which Pfand",
          dataIndex: "pfand_flow_cents",
          align: "right" as const,
          render: (v: number) =>
            v !== 0 ? (
              <Text type="secondary">{formatSignedCents(v)}</Text>
            ) : (
              <Text type="secondary">—</Text>
            ),
          width: 140,
        },
        {
          title: "What",
          key: "kind_and_description",
          render: (_: unknown, row) => (
            <Space>
              <Tag color={ACTIVITY_KIND_COLORS[row.kind]}>{row.kind}</Tag>
              <Text>{row.description}</Text>
            </Space>
          ),
        },
        {
          title: "Link",
          dataIndex: "link",
          width: 140,
          render: (_: unknown, row) => {
            if (!row.link) return <Text type="secondary">—</Text>;
            if (row.link.type === "procurement") {
              return (
                <Link to={`/stock/procurement/${row.link.id}`}>
                  Procurement #{row.link.id}
                </Link>
              );
            }
            if (row.link.type === "settlement") {
              return (
                <Link to={`/settlements?focus=${row.link.id}`}>
                  Settlement #{row.link.id}
                </Link>
              );
            }
            return (
              <Link to={`/stock/expenses?focus=${row.link.id}`}>
                Expense #{row.link.id}
              </Link>
            );
          },
        },
      ];

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  const lastCount = state?.last_count ?? null;
  const lastCountDaysAgo = lastCount
    ? Math.max(0, dayjs().diff(dayjs(lastCount.recorded_at), "day"))
    : null;

  // ---------------------------------------------------------------------------
  // Setup gate: render the "Set up the cash drawer" experience until the
  // operator records the opening balance. Detection: the server-side
  // ``active_opening_count`` scalar is the authoritative signal — the
  // older ``recent_activity.some(kind === "opening")`` check failed in
  // edge cases (very long histories where the 50-row feed window doesn't
  // reach the opening).
  // ---------------------------------------------------------------------------
  const isOpened = (state?.active_opening_count ?? 0) >= 1;

  // Initial loading: show a centered spinner until both /state and
  // /expense-categories resolve at least once.
  if (loading && state === null) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  if (state !== null && !isOpened) {
    return (
      <>
        {error && (
          <Alert
            type="error"
            message={error}
            style={{ marginBottom: 16 }}
            showIcon
            closable
            onClose={() => setError(null)}
          />
        )}
        <CashDrawerSetup onOpened={refresh} />
      </>
    );
  }

  return (
    <Card styles={{ body: { padding: isMobile ? 10 : undefined } }}>
      <Title level={2} style={{ marginTop: 0, marginBottom: 16 }}>
        Cash drawer
      </Title>

      {error && (
        <Alert
          type="error"
          message={error}
          style={{ marginBottom: 16 }}
          showIcon
          closable
          onClose={() => setError(null)}
        />
      )}

      {state && state.active_opening_count > 1 && (
        <Alert
          type="warning"
          showIcon
          message={`Multiple cash-drawer-opening rows detected (${state.active_opening_count}). The expected balance still sums them all, but only one opening row is correct — review the Expenses page and soft-delete the extras.`}
          style={{ marginBottom: 16 }}
        />
      )}

      {/* TEMPORARY purchase-based partial-settle widget (self-contained; remove
          this line + PurchaseSettleWidget.tsx once the article-reference
          settlement standard lands). Lives at the TOP — the primary action. */}
      <PurchaseSettleWidget onSettled={() => void refresh()} />

      <Title level={4} style={{ marginTop: 64, marginBottom: 12 }}>
        Cash Flow in Drawer
      </Title>
      {/* Drawer activity. scroll-x keeps it usable on a phone instead of
          squishing the columns. */}
      <Table<CashActivityRow>
        columns={activityColumns}
        dataSource={state?.recent_activity ?? []}
        rowKey={(row) =>
          `${row.when}-${row.kind}-${row.link?.type ?? "none"}-${
            row.link?.id ?? "none"
          }`
        }
        loading={loading}
        pagination={{ pageSize: 25 }}
        size="small"
        scroll={isMobile ? undefined : { x: "max-content" }}
        style={{ marginBottom: 24 }}
      />

      {/* ---- Drawer summary + actions, at the foot of the page ---- */}
      {/* Compact stat cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
          gap: 12,
          marginBottom: 16,
        }}
      >
        <Card size="small">
          <Statistic
            title="Expected balance"
            value={(state?.expected_balance_cents ?? 0) / 100}
            precision={2}
            suffix="€"
            valueStyle={{ fontSize: 18 }}
          />
          <Tooltip title="Derived from the bookkeeping (Σ Pfand-allocated portion of drawer-affecting expense rows). Not a count you enter.">
            <Text type="secondary" style={{ fontSize: 11 }}>
              Of which Pfand: {formatCents(state?.expected_pfand_cents ?? 0)}
            </Text>
          </Tooltip>
        </Card>
        <Card size="small">
          <Statistic
            title="Last count"
            value={lastCount ? lastCount.counted_cents / 100 : 0}
            precision={2}
            suffix="€"
            valueStyle={{ fontSize: 18 }}
          />
          <Text type="secondary" style={{ fontSize: 11 }}>
            {lastCount
              ? `${lastCountDaysAgo} day${lastCountDaysAgo === 1 ? "" : "s"} ago`
              : "Never counted"}
          </Text>
        </Card>
        <Card size="small">
          <Statistic
            title="Variance from last count"
            value={lastCount ? lastCount.variance_cents / 100 : 0}
            precision={2}
            suffix="€"
            valueStyle={{
              fontSize: 18,
              color: lastCount
                ? lastCount.variance_cents > 0
                  ? "#52c41a"
                  : lastCount.variance_cents < 0
                    ? "#cf1322"
                    : undefined
                : undefined,
            }}
          />
          <Text type="secondary" style={{ fontSize: 11 }}>
            {lastCount && lastCount.variance_cents === 0
              ? "Drawer matched expected"
              : lastCount
                ? "Allocated to Cash variance"
                : "—"}
          </Text>
        </Card>
      </div>

      {/* Historical snapshot + month-end overview */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
          gap: 12,
          marginBottom: 16,
        }}
      >
        <Card
          size="small"
          title="Historical balance"
          extra={
            snapshotAt ? (
              <Button
                size="small"
                type="link"
                onClick={() => setSnapshotAt(null)}
              >
                clear
              </Button>
            ) : null
          }
        >
          <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 8 }}>
            Expected cash in the drawer at a given date + time. Walks the
            same settlements + drawer-affecting expenses as the current
            balance, filtered to rows whose event timestamp is at or before
            the picked instant.
          </Paragraph>
          <DatePicker
            showTime
            allowClear
            value={snapshotAt}
            onChange={setSnapshotAt}
            format="DD.MM.YYYY HH:mm"
            placeholder="Pick a date & time"
            style={{ width: "100%", marginBottom: 12 }}
          />
          {snapshotError ? (
            <Alert type="error" showIcon message={snapshotError} />
          ) : snapshotLoading ? (
            <Spin size="small" />
          ) : snapshot ? (
            <div>
              <Statistic
                title={`Expected at ${dayjs(snapshot.at).format("DD.MM.YYYY HH:mm")}`}
                value={snapshot.expected_balance_cents / 100}
                precision={2}
                suffix="€"
                valueStyle={{ fontSize: 22 }}
              />
              <Text type="secondary" style={{ fontSize: 11 }}>
                Of which Pfand: {formatCents(snapshot.expected_pfand_cents)}
              </Text>
            </div>
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Pick a date to see the balance at that moment.
            </Text>
          )}
        </Card>

        <Card
          size="small"
          title="Month-end overview"
          extra={
            <Space size={4}>
              <Button
                size="small"
                onClick={() => setMonthEndYear((y) => y - 1)}
                // Backend rejects year < 2000 with HTTP 400. Clamp
                // client-side so the '‹' button can't step below.
                disabled={monthEndYear <= 2000}
              >
                ‹
              </Button>
              <Text strong>{monthEndYear}</Text>
              <Button
                size="small"
                onClick={() => setMonthEndYear((y) => y + 1)}
                disabled={monthEndYear >= new Date().getUTCFullYear()}
              >
                ›
              </Button>
            </Space>
          }
        >
          {monthEndError ? (
            <Alert
              type="error"
              showIcon
              message={monthEndError}
              style={{ marginBottom: 8 }}
            />
          ) : null}
          <Table<CashDrawerMonthEndRow>
            size="small"
            rowKey="month"
            loading={monthEndLoading}
            dataSource={monthEnd}
            pagination={false}
            columns={[
              {
                title: "Month",
                dataIndex: "month",
                sorter: (a, b) => a.month.localeCompare(b.month),
                defaultSortOrder: "ascend",
                render: (m: string) => dayjs(m + "-01").format("MMM YYYY"),
              },
              {
                title: "Balance",
                dataIndex: "expected_balance_cents",
                align: "right",
                sorter: (a, b) =>
                  a.expected_balance_cents - b.expected_balance_cents,
                render: (v: number) => formatCents(v),
              },
              {
                title: "Of which Pfand",
                dataIndex: "expected_pfand_cents",
                align: "right",
                sorter: (a, b) =>
                  a.expected_pfand_cents - b.expected_pfand_cents,
                filters: [
                  { text: "Non-zero Pfand", value: "nonzero" },
                  { text: "Zero Pfand", value: "zero" },
                ],
                onFilter: (val, row) =>
                  val === "nonzero"
                    ? row.expected_pfand_cents !== 0
                    : row.expected_pfand_cents === 0,
                render: (v: number) => formatCents(v),
              },
            ]}
            locale={{
              emptyText:
                "No months to show for this year (all in the future?)",
            }}
          />
        </Card>
      </div>

      {/* Quick actions — directly above Count cash */}
      <Space style={{ marginBottom: 16, flexWrap: "wrap" }}>
        <Button onClick={openExpenseModal}>+ Cash expense</Button>
        <Button onClick={() => void openProcurementModal()}>
          + Pay procurement
        </Button>
        <Button onClick={openBulkBuyModal}>+ Bulk buy</Button>
      </Space>

      <div>
        <Button
          type="primary"
          size="large"
          onClick={() => setCountModalOpen(true)}
          disabled={loading}
        >
          + Count cash
        </Button>
      </div>

      {/* ---------- Count cash modal ---------- */}
      <Modal
        title="Count cash drawer"
        open={countModalOpen}
        onOk={() => void submitCount()}
        confirmLoading={saving}
        onCancel={() => {
          setCountModalOpen(false);
          setCountedCents(null);
          setCountComment("");
        }}
        okText="Save count"
        destroyOnHidden
      >
        <Paragraph>
          Count every coin and note in the drawer and enter ONE total below.
          The "of which Pfand" stat is computed from the bookkeeping — don't
          try to separate it physically.
        </Paragraph>
        <Form layout="vertical">
          <Form.Item label="Counted amount" required>
            <InputNumber<number>
              min={0}
              step={1}
              precision={0}
              addonAfter="ct"
              placeholder="e.g. 12345"
              style={{ width: "100%" }}
              value={countedCents ?? undefined}
              onChange={(v) =>
                setCountedCents(
                  v === null || v === undefined ? null : Math.round(v),
                )
              }
            />
          </Form.Item>
          <Form.Item label="Comment (optional)">
            <TextArea
              rows={2}
              maxLength={500}
              value={countComment}
              onChange={(e) => setCountComment(e.target.value)}
            />
          </Form.Item>
          <Space
            direction="vertical"
            style={{
              background: "rgba(0,0,0,0.03)",
              padding: 12,
              borderRadius: 4,
              width: "100%",
            }}
          >
            <Text>
              Expected: <Text strong>{formatCents(expectedCents)}</Text>
            </Text>
            <Text>
              Variance:{" "}
              <Text
                strong
                style={{
                  color:
                    liveVariance !== null && liveVariance > 0
                      ? "#52c41a"
                      : liveVariance !== null && liveVariance < 0
                        ? "#cf1322"
                        : undefined,
                }}
              >
                {liveVariance !== null ? formatSignedCents(liveVariance) : "—"}
              </Text>
            </Text>
          </Space>
        </Form>
      </Modal>

      {/* ---------- Cash expense modal ---------- */}
      <Modal
        title="Record cash expense"
        open={expenseModalOpen}
        onOk={() => void submitExpense()}
        confirmLoading={saving}
        onCancel={() => setExpenseModalOpen(false)}
        okText="Save"
        destroyOnHidden
      >
        <Form<CashExpenseFormValues>
          form={expenseForm}
          layout="vertical"
          initialValues={{
            direction: "out",
            value_magnitude: null,
            pfand_magnitude: null,
          }}
        >
          <Form.Item
            label="Direction"
            name="direction"
            rules={[{ required: true, message: "Direction is required" }]}
          >
            <Radio.Group buttonStyle="solid">
              <Radio.Button
                value="in"
                style={
                  watchedDirection === "in"
                    ? { borderColor: "#52c41a", color: "#52c41a" }
                    : {}
                }
              >
                ↑ In (cash entering drawer)
              </Radio.Button>
              <Radio.Button
                value="out"
                style={
                  watchedDirection === "out"
                    ? { borderColor: "#cf1322", color: "#cf1322" }
                    : {}
                }
              >
                ↓ Out (cash leaving drawer)
              </Radio.Button>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            label="Value"
            name="value_magnitude"
            tooltip="Non-Pfand cash portion in cents. Sign comes from the direction above."
          >
            <InputNumber
              min={0}
              step={1}
              addonAfter="ct"
              style={{ width: "100%" }}
            />
          </Form.Item>
          <Form.Item
            label="Pfand"
            name="pfand_magnitude"
            tooltip="Independent allocation for THIS event — not the drawer's overall Pfand, in cents. Leave empty for no Pfand component."
          >
            <InputNumber
              min={0}
              step={1}
              addonAfter="ct"
              style={{ width: "100%" }}
            />
          </Form.Item>
          <Form.Item
            label="Category"
            name="category_id"
            rules={[{ required: true, message: "Category is required" }]}
          >
            <Select
              options={categories
                .filter(
                  (c) =>
                    c.active &&
                    !(
                      OPERATOR_HIDDEN_FOR_CASH_DRAWER as readonly string[]
                    ).includes(c.code),
                )
                .map((c) => ({
                  value: c.id,
                  label: c.name + (c.system_seeded ? " (system)" : ""),
                }))}
            />
          </Form.Item>
          <Form.Item label="Comment" name="comment">
            <TextArea rows={2} maxLength={500} />
          </Form.Item>
        </Form>
      </Modal>

      {/* ---------- Pay procurement modal ---------- */}
      <Modal
        title="Pay procurement from drawer"
        open={procurementModalOpen}
        onOk={() => void submitProcurementPayment()}
        confirmLoading={saving}
        onCancel={() => {
          setProcurementModalOpen(false);
          setSelectedProcurementId(null);
          setProcurementComment("");
        }}
        okText="Pay from drawer"
        okButtonProps={{ disabled: selectedProcurementId === null }}
        destroyOnHidden
      >
        <Paragraph>
          Pick a received procurement that hasn't yet been paid out of the
          drawer. A negative ``Cash drawer`` expense will be recorded for the
          goods + Pfand totals.
        </Paragraph>
        <Form layout="vertical">
          <Form.Item label="Procurement" required>
            <Select<number>
              showSearch
              placeholder="Select a received procurement"
              optionFilterProp="label"
              value={selectedProcurementId ?? undefined}
              onChange={(v) => setSelectedProcurementId(v)}
              options={procurements
                .filter((p) => unpaidProcurementIds.has(p.id))
                .map((p) => ({
                  value: p.id,
                  label: `#${p.id} — ${p.supplier ?? "(no supplier)"} — ${formatCents(
                    p.total_cost_cents + p.total_pfand_cents,
                  )}`,
                }))}
              notFoundContent={
                procurements.length === 0
                  ? "No received procurements found"
                  : "All received procurements already paid"
              }
            />
          </Form.Item>
          {selectedProcurement && (
            <Space
              direction="vertical"
              style={{
                background: "rgba(0,0,0,0.03)",
                padding: 12,
                borderRadius: 4,
                width: "100%",
                marginBottom: 12,
              }}
            >
              <Text>
                Goods:{" "}
                <Text strong>
                  {formatCents(selectedProcurement.total_cost_cents)}
                </Text>
              </Text>
              <Text>
                Pfand:{" "}
                <Text strong>
                  {formatCents(selectedProcurement.total_pfand_cents)}
                </Text>
              </Text>
              <Text>
                Grand total:{" "}
                <Text strong>
                  {formatCents(
                    selectedProcurement.total_cost_cents +
                      selectedProcurement.total_pfand_cents,
                  )}
                </Text>
              </Text>
            </Space>
          )}
          <Form.Item label="Comment (optional)">
            <TextArea
              rows={2}
              maxLength={500}
              value={procurementComment}
              onChange={(e) => setProcurementComment(e.target.value)}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* ---------- Bulk-buy modal ---------- */}
      <Modal
        title="Bulk buy for a user"
        open={bulkBuyOpen}
        onOk={() => void submitBulkBuy()}
        okText={
          bulkBuySelectedChain
            ? `Buy ${bulkBuyQty} × ${bulkBuySelectedChain.name}`
            : "Buy"
        }
        okButtonProps={{
          disabled:
            bulkBuyUserId === null ||
            bulkBuySelectedChain === null ||
            bulkBuyQty < 1,
          loading: bulkBuySubmitting,
        }}
        onCancel={() => setBulkBuyOpen(false)}
        destroyOnHidden
      >
        <Paragraph type="secondary">
          Charge N × an article to a strichliste user directly from the
          drawer — same effect as tapping the article N times at the kiosk,
          but as one transaction row. Undo via the user's transactions page.
        </Paragraph>
        <Form layout="vertical">
          <Form.Item label="User" required>
            <Select
              showSearch
              placeholder="Search by name / email"
              value={bulkBuyUserId ?? undefined}
              onChange={(v) => setBulkBuyUserId(v ?? null)}
              onSearch={setBulkBuyUserSearch}
              filterOption={false}
              options={bulkBuyUserOptions.map((u) => ({
                value: u.value,
                label: `${u.label}${u.balance_cents !== null ? ` — ${formatCents(u.balance_cents)}` : ""}`,
              }))}
              notFoundContent={
                bulkBuyUserSearch.trim()
                  ? "No users match"
                  : "Type to search"
              }
              style={{ width: "100%" }}
            />
          </Form.Item>
          <Form.Item label="Article" required>
            <Select
              showSearch
              placeholder="Search catalog"
              value={bulkBuyChainId ?? undefined}
              onChange={(v) => setBulkBuyChainId(v ?? null)}
              optionFilterProp="label"
              options={bulkBuyChains.map((c) => ({
                value: c.chain_root_id,
                label: `${c.name} — ${formatCents(c.sell_price_cents)}`,
              }))}
              style={{ width: "100%" }}
            />
          </Form.Item>
          <Form.Item label="Quantity" required>
            <Space>
              <Button
                onClick={() => setBulkBuyQty((q) => Math.max(1, q - 1))}
                disabled={bulkBuyQty <= 1}
              >
                −
              </Button>
              <InputNumber<number>
                min={1}
                max={99}
                step={1}
                precision={0}
                value={bulkBuyQty}
                onChange={(v) =>
                  setBulkBuyQty(
                    v === null || v === undefined
                      ? 1
                      : Math.max(1, Math.min(99, Math.round(v))),
                  )
                }
                style={{ width: 100, textAlign: "center" }}
              />
              <Button
                onClick={() => setBulkBuyQty((q) => Math.min(99, q + 1))}
                disabled={bulkBuyQty >= 99}
              >
                +
              </Button>
            </Space>
          </Form.Item>
          {bulkBuySelectedChain ? (
            <Paragraph style={{ fontSize: 16 }}>
              {bulkBuyQty} × {formatCents(bulkBuySelectedChain.sell_price_cents)}{" "}
              = <b>{formatCents(bulkBuyTotalCents)}</b>
            </Paragraph>
          ) : null}
        </Form>
      </Modal>
    </Card>
  );
};

export default FinanceCashPage;
